import React from "react";
import { connect } from "react-redux";

import { faArrowPointer } from "@fortawesome/pro-solid-svg-icons";
import { faEnvelopeOpen } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { areEmailMetricsVisible } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import styles from "./EmailsMetrics.module.scss";

const messages = defineMessages({
  clickedTooltip: {
    id: "component.emailsMetrics.clickedTooltip",
    defaultMessage:
      "{count} of {total} recipients clicked a link{undelivered, plural, =0 {} other { (# not delivered)}}",
    description:
      "Tooltip on the aggregate link-clicked chip, noting how many recipients never received the email",
  },
  openedTooltip: {
    id: "component.emailsMetrics.openedTooltip",
    defaultMessage:
      "{count} of {total} recipients opened this email{undelivered, plural, =0 {} other { (# not delivered)}}",
    description:
      "Tooltip on the aggregate opened chip, noting how many recipients never received the email",
  },
});

// Counts come in as numbers rather than as the email log array, because a bulk-email
// parent has no loaded children to count — its totals arrive server-side on
// `activity.emailStats`. Callers that do hold the array use getEmailMetricsCounts.
interface EmailsMetricsProps {
  clicked: number;
  opened: number;
  total: number;
  undelivered?: number;
}

const EmailsMetrics = ({
  clicked,
  opened,
  total,
  undelivered = 0,
  visible,
}: EmailsMetricsProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();

  if (!visible) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Tooltip
        title={intl.formatMessage(messages.openedTooltip, { count: opened, total, undelivered })}
      >
        <div className={cn(styles.chip, { [styles.chipActive]: opened > 0 })}>
          <FontAwesomeIcon className={styles.chipIcon} icon={faEnvelopeOpen} />
          <span className={styles.chipLabel}>
            {opened}/{total}
          </span>
        </div>
      </Tooltip>
      <Tooltip
        title={intl.formatMessage(messages.clickedTooltip, { count: clicked, total, undelivered })}
      >
        <div className={cn(styles.chip, { [styles.chipActive]: clicked > 0 })}>
          <FontAwesomeIcon className={styles.chipIcon} icon={faArrowPointer} />
          <span className={styles.chipLabel}>
            {clicked}/{total}
          </span>
        </div>
      </Tooltip>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  visible: areEmailMetricsVisible(state),
});

export default connect(mapStateToProps)(EmailsMetrics);
